<template>
   <el-dialog
      :title="dialogTitle"
      v-model="dialogVisible"
      width="560px"
      :modal-append-to-body="false"
      :close-on-click-modal="false"
      custom-class="link-crowd-dialog"
      @close="handleClose"
   >
      <!-- 非编辑：仅展示已选人群标签 -->
      <div v-if="!isEdit" class="link-crowd-body link-crowd-body--view">
         <div class="link-crowd-tags">
            <el-tag
               v-for="(t, i) in viewTagList"
               :key="i"
               type="info"
               class="link-crowd-view-tag"
            >
               {{ t }}
            </el-tag>
            <span v-if="viewTagList.length === 0" class="link-crowd-empty">暂无关联人群</span>
         </div>
      </div>

      <!-- 编辑：多选人群 -->
      <div v-else class="link-crowd-body link-crowd-body--edit">
         <el-form label-position="left" label-width="100px">
            <el-form-item>
               <template slot="label">
                  <span class="required">*</span> 适用人群
               </template>
               <div class="link-crowd-edit-inner">
                  <!-- 支持多选：交互为点击切换，不在界面展示说明文案 -->
                  <div class="crowd-options">
                     <div
                        v-for="opt in crowdOptions"
                        :key="opt.id"
                        class="crowd-option"
                        :class="{ 'is-selected': isCrowdSelected(opt.id) }"
                        @click="toggleCrowd(opt.id)"
                     >
                        {{ opt.name }}
                     </div>
                  </div>
               </div>
            </el-form-item>
         </el-form>
      </div>

      <span slot="footer" class="dialog-footer link-crowd-footer">
         <template v-if="!isEdit">
            <el-button type="primary" @click="enterEdit">编辑</el-button>
         </template>
         <template v-else>
            <el-button @click="cancelEdit">取消</el-button>
            <el-button type="primary" @click="handleSave">保存</el-button>
         </template>
      </span>
   </el-dialog>
</template>

<script>
const API_SUCCESS = 10000;

export default {
   name: "LinkCrowdDialog",
   data() {
      return {
         dialogVisible: false,
         isEdit: false,
         currentRow: null,
         crowdOptions: [],
         selectedIds: [],
         snapshotIds: [],
      };
   },
   computed: {
      dialogTitle() {
         return this.isEdit ? "编辑关联人群" : "适用人群";
      },
      viewTagList() {
         if (!this.currentRow) return [];
         if (this.selectedIds.length) {
            return this.selectedIds
               .map((id) => {
                  const o = this.crowdOptions.find((x) => x.id === id);
                  return o ? o.name : "";
               })
               .filter(Boolean);
         }
         const s = this.currentRow.crowd;
         if (!s || typeof s !== "string") return [];
         return s.split(/[、,，]/).map((x) => x.trim()).filter(Boolean);
      },
   },
   methods: {
      normalizeListResponse(data) {
         if (!data) return [];
         if (Array.isArray(data)) return data;
         if (Array.isArray(data.list)) return data.list;
         return [];
      },
      loadCrowdOptions() {
         return this.$http({
            url: this.$util.domain + "/adminapi/v1/ad/crowd/all",
            method: "GET",
         })
            .then((res) => {
               if (res.body.error !== API_SUCCESS) {
                  this.crowdOptions = [];
                  return;
               }
               const raw = this.normalizeListResponse(res.body.data);
               const list = raw.length ? raw : res.body.data && res.body.data.id != null ? [res.body.data] : [];
               this.crowdOptions = list
                  .map((c) => ({
                     id: c.id,
                     name: c.crowdName || "",
                  }))
                  .filter((x) => x.id != null && x.name);
            })
            .catch(() => {
               this.crowdOptions = [];
            });
      },
      show(row) {
         this.currentRow = row || null;
         this.isEdit = false;
         this.dialogVisible = true;
         this.loadCrowdOptions().then(() => {
            this.syncSelectedFromRow();
            this.snapshotIds = [...this.selectedIds];
         });
      },
      /** 优先使用行上 crowdIds（接口/保存回写），否则用 crowd 文案反查选项 id */
      syncSelectedFromRow() {
         const row = this.currentRow;
         if (!row) {
            this.selectedIds = [];
            return;
         }
         if (row.crowdIds && Array.isArray(row.crowdIds) && row.crowdIds.length) {
            const valid = row.crowdIds.filter((id) =>
               this.crowdOptions.some((o) => o.id === id || String(o.id) === String(id))
            );
            this.selectedIds = [...new Set(valid)];
            return;
         }
         this.selectedIds = this.parseCrowdToIds(row.crowd);
      },
      parseCrowdToIds(crowdStr) {
         if (!crowdStr || typeof crowdStr !== "string") return [];
         const parts = crowdStr.split(/[、,，]/).map((s) => s.trim()).filter(Boolean);
         const sortedOpts = [...this.crowdOptions].sort((a, b) => b.name.length - a.name.length);
         const ids = [];
         for (const p of parts) {
            let opt = sortedOpts.find((o) => o.name === p);
            if (!opt) opt = sortedOpts.find((o) => p.includes(o.name) || o.name.includes(p));
            if (opt) ids.push(opt.id);
         }
         return [...new Set(ids)];
      },
      enterEdit() {
         this.syncSelectedFromRow();
         this.snapshotIds = [...this.selectedIds];
         this.isEdit = true;
      },
      cancelEdit() {
         this.selectedIds = [...this.snapshotIds];
         this.isEdit = false;
      },
      isCrowdSelected(id) {
         return this.selectedIds.includes(id);
      },
      toggleCrowd(id) {
         if (this.isCrowdSelected(id)) {
            this.selectedIds = this.selectedIds.filter((x) => x !== id);
         } else {
            this.selectedIds = [...this.selectedIds, id];
         }
      },
      handleSave() {
         if (!this.selectedIds.length) {
            return this.$message.warning("请至少选择一个适用人群");
         }
         if (!this.currentRow || this.currentRow.id == null) {
            return this.$message.warning("策略信息缺失");
         }
         this.$http({
            url: `${this.$util.domain}/adminapi/v1/ad/strategy/${this.currentRow.id}/crowd`,
            method: "POST",
            dataType: "json",
            body: JSON.stringify({
               crowdIds: [...this.selectedIds],
               operator: 0,
            }),
         })
            .then((res) => {
               if (res.body.error !== API_SUCCESS) {
                  this.$message({
                     type: "error",
                     message: res.body.message || "保存失败",
                  });
                  return;
               }
               const names = this.selectedIds
                  .map((id) => {
                     const o = this.crowdOptions.find((x) => x.id === id);
                     return o ? o.name : "";
                  })
                  .filter(Boolean);
               const crowdText = names.join("、");
               this.$emit("saved", {
                  row: this.currentRow,
                  crowdIds: [...this.selectedIds],
                  crowdText,
               });
               this.$message({
                  type: "success",
                  message: "操作成功!",
               });
               this.snapshotIds = [...this.selectedIds];
               this.isEdit = false;
               this.dialogVisible = false;
            })
            .catch(() => {
               this.$message({
                  type: "error",
                  message: "操作失败，请稍后再试!",
               });
            });
      },
      handleClose() {
         this.isEdit = false;
         this.currentRow = null;
         this.selectedIds = [];
         this.snapshotIds = [];
      },
   },
};
</script>

<style scoped>
.required {
   color: #f56c6c;
   font-weight: 600;
   margin-right: 4px;
}

.link-crowd-body {
   min-height: 120px;
}

.link-crowd-tags {
   display: flex;
   flex-wrap: wrap;
   gap: 10px 12px;
   align-items: flex-start;
   padding: 4px 0;
}

.link-crowd-view-tag {
   border-radius: 4px;
}

.link-crowd-empty {
   color: #909399;
   font-size: 14px;
}

.link-crowd-edit-inner {
   position: relative;
   width: 100%;
}

.crowd-options {
   display: flex;
   flex-wrap: wrap;
   gap: 10px 12px;
   align-items: flex-start;
}

.crowd-option {
   border: 1px solid #c0ccda;
   border-radius: 4px;
   padding: 6px 10px;
   font-size: 13px;
   line-height: 1.3;
   cursor: pointer;
   user-select: none;
   background: #fff;
   display: inline-flex;
   align-items: center;
   justify-content: center;
}

.crowd-option.is-selected {
   border-color: #409eff;
   color: #409eff;
   background: #ecf5ff;
}

.link-crowd-footer {
   display: flex;
   justify-content: center;
   align-items: center;
   gap: 12px;
}
</style>



// WEBPACK FOOTER //
// LinkCrowdDialog.vue?ed58644a